import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import useTimer from "easytimer-react-hook";

import "./Game.css";
import { ALIAS_TO_COUNTRY, COUNTRIES, GAME_STATES } from "./Constants";
import MapResults from "./MapResults";
import { standardizeGuess } from "./Standardize";
import TextResults from "./TextResults";

type GameProps = {
  mapless: boolean;
  initialGuessedCountries?: Set<string>;
};

function Game(props: GameProps) {
  const gameRef = useRef<HTMLDivElement>(null);
  const [gameState, setGameState] = useState(GAME_STATES.IDLE);
  const [guess, setGuess] = useState("");
  const [guessedCountries, setGuessedCountries] = useState(props.initialGuessedCountries || new Set<string>());
  const [lastMatch, setLastMatch] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [timer] = useTimer();

  function startGame() {
    setGameState(GAME_STATES.PLAYING);
    timer.start();
  }

  const endGame = useCallback((won: boolean) => {
    setGameState(won ? GAME_STATES.WON : GAME_STATES.LOST);
    setGuess("");
    setLastMatch("");
    timer.pause();
  }, [timer]);

  function resetGame() {
    setGameState(GAME_STATES.IDLE);
    setGuess("");
    setGuessedCountries(new Set());
    setLastMatch("");
    timer.reset();
    timer.pause(); // easytimer.js is strange in that reset automatically restarts the timer
  }

  function handleGuess(newGuess: string) {
    setGuess(newGuess);

    const match = ALIAS_TO_COUNTRY.get(standardizeGuess(newGuess));
    if (match) {
      if (!guessedCountries.has(match)) {
        setGuessedCountries((oldValue => new Set([...oldValue, match])));
        setLastMatch(match);
        setGuess("");
      }
    }
  }

  function toggleFullscreen() {
    if (getFullscreenElement()) {
      exitFullscreen();
    } else if (gameRef.current) {
      requestFullscreen(gameRef.current);
    }
  }

  useEffect(() => {
    if (guessedCountries.size === COUNTRIES.length) {
      endGame(true);
    }
  }, [guessedCountries, endGame]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(Boolean(getFullscreenElement()));
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  return (
    <div className="container game" ref={gameRef}>
      { !props.mapless && (
        <Button
          variant="outline-secondary"
          size="sm"
          className="fullscreen-toggle"
          onClick={toggleFullscreen}
        >
          { isFullscreen ? "Exit full screen" : "Go full screen" }
        </Button>
      )}
      <div className="row align-items-center controls">
        <div className="col-sm-6">
          <div className="m-2">
            <Form onSubmit={(event) => event.preventDefault()}>
              <Form.Group>
                {
                  gameState === GAME_STATES.PLAYING &&
                    <Form.Control
                      value={guess}
                      onChange={(event) => handleGuess(event.target.value)}
                      autoFocus
                    />
                }
                {
                  [GAME_STATES.IDLE, GAME_STATES.LOST].includes(gameState) &&
                    <Form.Control disabled />
                }
                {
                  gameState === GAME_STATES.WON &&
                    <span className="won">YOU WIN IN {timer.getTimeValues().toString(["minutes", "seconds"])}!</span>
                }
              </Form.Group>
            </Form>
          </div>
        </div>
        <div className="col-sm-2">
          <div className="m-2">
            { gameState === GAME_STATES.IDLE && <Button onClick={startGame}>Start!</Button> }
            { gameState === GAME_STATES.PLAYING && <Button onClick={() => endGame(false)}>End</Button> }
            { [GAME_STATES.WON, GAME_STATES.LOST].includes(gameState) && <Button onClick={resetGame}>Reset</Button> }
          </div>
        </div>
        <div className="col-sm-2">
          <div className="m-2">
            { guessedCountries.size } / { COUNTRIES.length }
          </div>
        </div>
        <div className="col-sm-2">
          <div className="m-2">
            {timer.getTimeValues().toString(["minutes", "seconds"])}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="m-1">
            {
              props.mapless ?
                <TextResults
                  guessedCountries={guessedCountries}
                  showMissed={gameState === GAME_STATES.LOST}
                  lastMatch={lastMatch}
                /> :
                <MapResults
                  guessedCountries={guessedCountries}
                  showMissed={gameState === GAME_STATES.LOST}
                  lastMatch={lastMatch}
                />
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// The Fullscreen API is still vendor-prefixed in Safari, so wrap the calls.
function requestFullscreen(element: HTMLElement) {
  const el = element as HTMLElement & { webkitRequestFullscreen?: () => void };
  if (el.requestFullscreen) {
    el.requestFullscreen();
  } else if (el.webkitRequestFullscreen) {
    el.webkitRequestFullscreen();
  }
}

function exitFullscreen() {
  const doc = document as Document & { webkitExitFullscreen?: () => void };
  if (doc.exitFullscreen) {
    doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen();
  }
}

function getFullscreenElement() {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  return document.fullscreenElement || doc.webkitFullscreenElement || null;
}

export default Game;
